import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Divider,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Close,
  Edit,
  Category as DefaultCategoryIcon,
  RestaurantMenu,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { motion } from 'framer-motion';

// Import all MUI icons for dynamic rendering
import * as MuiIcons from '@mui/icons-material';

const ViewDetails = ({ open, onClose, category, onEdit }) => {
  const { t } = useTranslation();
  const MotionBox = motion.div;

  if (!category) return null;

   // Add debugging here
  console.log('🔍 ViewDetails Debug:');
  console.log('category.itemCount:', category.itemCount);
  console.log('typeof itemCount:', typeof category.itemCount);
  console.log('Translation result:', t('category.itemsCountShort', { count: category.itemCount || 0 }));
  
  // Get icon component by name
  const getIconByName = (iconName) => {
    if (!iconName) return <DefaultCategoryIcon />;
    
    // Map of friendly names to MUI icon component names
    const iconMap = {
      'Fastfood': 'Fastfood',
      'Drinks': 'LocalDrink',
      'Dessert': 'Cake',
      'Coffee': 'Coffee',
      'Menu': 'RestaurantMenu',
      'Bar': 'LocalBar',
      'Tea': 'EmojiFoodBeverage',
      'Ice Cream': 'Icecream',
      'Pizza': 'LocalPizza',
      'Dining': 'LocalDining',
      'Ramen': 'RamenDining',
      'Sushi': 'SetMeal',
      'Lunch': 'LunchDining',
      'Restaurant': 'Restaurant',
      'Wine': 'Liquor',
      'Soup': 'SoupKitchen',
      'Breakfast': 'BreakfastDining',
      'Bakery': 'BakeryDining',
      'Dinner': 'DinnerDining',
      'Egg': 'EggAlt',
      'Kebab': 'KebabDining',
      'Tapas': 'SportsBar'
    };
    
    // Get the component name from our map
    const componentName = iconMap[iconName];
    
    // Try to get the icon from MUI icons
    if (componentName && MuiIcons[componentName]) {
      const IconComponent = MuiIcons[componentName];
      return <IconComponent />;
    }
    
    // Fallback for icons that don't exist
    return <DefaultCategoryIcon />;
  };

  const selectedIcon = getIconByName(category.icon);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t('category.detailsTitle')}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ padding: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar 
                  sx={{ 
                    width: 48, 
                    height: 48,
                    bgcolor: category.colorValue || 'primary.main',
                    mr: 2
                  }}
                >
                  {selectedIcon}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {category.name}
                  </Typography>
                  {category.icon && (
                    <Chip 
                      size="small" 
                      label={category.icon}
                      sx={{ mt: 0.5 }}
                      icon={selectedIcon}
                    />
                  )}
                </Box>
              </Box>
              
              <Divider sx={{ mb: 3 }} />
              
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" sx={{ width: '40%', fontWeight: 500 }}>
                        {t('category.categoryId')}
                      </TableCell>
                      <TableCell>{category.category_id}</TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell component="th" sx={{ width: '40%', fontWeight: 500 }}>
                        {t('category.name')}
                      </TableCell>
                      <TableCell>{category.name}</TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell component="th" sx={{ width: '40%', fontWeight: 500 }}>
                        {t('category.icon')}
                      </TableCell>
                      <TableCell>
                        {category.icon ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ color: category.colorValue, mr: 1 }}>
                              {selectedIcon}
                            </Box>
                            {category.icon}
                          </Box>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell component="th" sx={{ width: '40%', fontWeight: 500 }}>
                        {t('category.color')}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box 
                            sx={{ 
                              width: 20, 
                              height: 20, 
                              borderRadius: '50%', 
                              bgcolor: category.colorValue || 'primary.main',
                              mr: 1
                            }} 
                          />
                          {category.colorName || t('category.defaultColor')}
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell component="th" sx={{ width: '40%', fontWeight: 500 }}>
                        {t('category.menuItems')}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={t('category.itemsCountShort', { count: category.itemCount || 0 })}
                          size="small" 
                          color={category.itemCount ? 'primary' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                    
                    {category.created_at && (
                      <TableRow>
                        <TableCell component="th" sx={{ width: '40%', fontWeight: 500 }}>
                          {t('category.created')}
                        </TableCell>
                        <TableCell>{new Date(category.created_at).toLocaleString()}</TableCell>
                      </TableRow>
                    )}
                    
                    {category.modified_at && (
                      <TableRow>
                        <TableCell component="th" sx={{ width: '40%', fontWeight: 500 }}>
                          {t('category.lastModified')}
                        </TableCell>
                        <TableCell>{new Date(category.modified_at).toLocaleString()}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {t('category.description')}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {category.description || t('category.noDescription')}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ padding: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {t('category.menuItemsInCategory')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {category.items && category.items.length > 0 ? (
                <TableContainer sx={{ maxHeight: 350, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>{t('category.no')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('category.itemName')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('category.status')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {category.items.map((item, index) => (
                        <TableRow key={item.item_id}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>
                            {item.available ? (
                              <Chip 
                                icon={<CheckCircle fontSize="small" />} 
                                label={t('status.available')} 
                                size="small" 
                                color="success" 
                                variant="outlined"
                              />
                            ) : (
                              <Chip 
                                icon={<Cancel fontSize="small" />}
                                label={t('status.unavailable')} 
                                size="small" 
                                color="error" 
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {t('category.noMenuItems')}
                  </Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 3 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Paper
                  elevation={3}
                  sx={{
                    width: 120,
                    height: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    borderLeft: `4px solid ${category.colorValue || 'primary.main'}`,
                    bgcolor: 'action.hover',
                    position: 'relative',
                    overflow: 'hidden',
                    mb: 2
                  }}
                >
                  {category.image ? (
                    <>
                      <Box 
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundImage: `url(${category.image})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          opacity: 0.3
                        }}
                      />
                      <Box
                        sx={{
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        {selectedIcon && (
                          <Box sx={{ color: category.colorValue || 'primary.main', fontSize: 36 }}>
                            {selectedIcon}
                          </Box>
                        )}
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                          {category.name}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      {selectedIcon ? (
                        <Box sx={{ color: category.colorValue || 'primary.main', fontSize: 36 }}>
                          {selectedIcon}
                        </Box>
                      ) : (
                        <RestaurantMenu sx={{ fontSize: 36, color: category.colorValue || 'primary.main' }} />
                      )}
                      <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                        {category.name}
                      </Typography>
                    </>
                  )}
                </Paper>
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Edit />}
                  onClick={() => {
                    onClose();
                    if (onEdit) onEdit(category);
                  }}
                  sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
                    }
                  }}
                >
                  {t('category.editButton')}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewDetails;